import { DEFAULT_LAYOUTS } from './defaults'
import type { FloorPlanLayout, LayoutSection, LayoutTable } from './types'

/** The part of GET /venues/:id the layout needs. */
export interface VenueLayoutSource {
  id?: string
  sections?: Array<{ id: string; name: string; colour?: string }>
  tables?: Array<{ id: number; label?: string; sectionId: string; capacity: number }>
  customLayout?: Partial<FloorPlanLayout> | null
  /**
   * True when the venue has JB & Sons' built-in floor plan (the organisation turned on its built-in venue
   * rules). A missing flag does not enable a built-in plan.
   */
  builtInLayout?: boolean
}

const COLOURS: LayoutSection['colour'][] = ['red', 'orange', 'blue', 'green']

const TABLE_W = 96
const TABLE_H = 58
const GAP = 16
const PAD = 20
const SECTION_GAP = 14
const CANVAS_W = 860

/**
 * A simple floor plan for a venue that has none saved: each section is a band, its tables in a grid inside.
 * Table labels are the table ids (the rest of the app reads a table's id from its label). Staff then move
 * things about in the floor plan editor.
 */
export function generateDefaultLayout(venue: VenueLayoutSource): FloorPlanLayout {
  const tables = venue.tables ?? []
  const sections = venue.sections?.length ? venue.sections : [{ id: 'inside', name: 'Inside', colour: 'red' }]
  const perRow = Math.max(1, Math.floor((CANVAS_W - 4 * PAD) / (TABLE_W + GAP)))

  const layoutSections: LayoutSection[] = []
  const layoutTables: LayoutTable[] = []
  let y = 10

  sections.forEach((section, index) => {
    const inSection = tables.filter((t) => t.sectionId === section.id)
    const rows = Math.max(1, Math.ceil(inSection.length / perRow))
    const height = PAD * 2 + rows * TABLE_H + (rows - 1) * GAP
    const colour = (COLOURS as string[]).includes(section.colour ?? '') ? (section.colour as LayoutSection['colour']) : COLOURS[index % COLOURS.length]!
    layoutSections.push({ kind: 'section', id: section.id, name: section.name, colour, x: 10, y, w: CANVAS_W - 20, h: height })
    inSection.forEach((table, i) => {
      layoutTables.push({
        kind: 'table',
        id: `t${table.id}`,
        label: String(table.id),
        capacity: table.capacity,
        sectionId: section.id,
        shape: 'rect',
        x: 10 + 2 * PAD + (i % perRow) * (TABLE_W + GAP),
        y: y + PAD + Math.floor(i / perRow) * (TABLE_H + GAP),
        w: TABLE_W,
        h: TABLE_H,
      })
    })
    y += height + SECTION_GAP
  })

  return { venueId: venue.id ?? '', canvasW: CANVAS_W, canvasH: Math.max(300, y), tables: layoutTables, sections: layoutSections, fixtures: [] }
}

/**
 * The layout to show for a venue: its saved layout, else JB & Sons' built-in plan when the venue has one
 * (see `builtInLayout`), else a generated one. Never another venue's plan.
 */
export function resolveVenueLayout(venueId: string, data: VenueLayoutSource): FloorPlanLayout {
  const base = data.builtInLayout === true ? DEFAULT_LAYOUTS[venueId] : undefined
  const custom = data.customLayout
  if (custom && custom.tables && custom.sections) {
    return {
      ...(custom as FloorPlanLayout),
      venueId: custom.venueId ?? venueId,
      canvasW: custom.canvasW || base?.canvasW || 860,
      canvasH: custom.canvasH || base?.canvasH || 640,
      fixtures: custom.fixtures ?? (base ? JSON.parse(JSON.stringify(base.fixtures)) : []),
    }
  }
  return base ?? generateDefaultLayout({ ...data, id: venueId })
}
